import { useEffect, useState } from "react";
import { Row, Card, CardBody } from "reactstrap";
import { motion } from "framer-motion";
import ReactApexChart from "react-apexcharts";

import { userMetricsApi, getMcapTimeOfDay } from "api/api.service";
import { getCached, setCached, mcapCacheKey } from "utils/mcapCache";
import KpiTile from "../components/KpiTile";
import typography from "../typography";

const GRAPH_COLORS = { teal: "#06402b", gridBorder: "#f6f6f6" };
const panelVariants = { hidden: { opacity: 0, scale: 0.98 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.3, ease: [0.23, 1, 0.32, 1] } } };
const DOW_LABELS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function PlatformHealth({ filters, filterBar }) {
  const [metrics, setMetrics] = useState({});
  const [timeOfDay, setTimeOfDay] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const cacheKey = mcapCacheKey("platform-health", filters);
    const cached = getCached(cacheKey);
    if (cached) {
      setMetrics(cached.metrics);
      setTimeOfDay(cached.timeOfDay);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    Promise.all([userMetricsApi(filters), getMcapTimeOfDay(filters)])
      .then(([metricsRes, timeOfDayRes]) => {
        if (cancelled) return;
        setMetrics(metricsRes || {});
        setTimeOfDay(timeOfDayRes || {});
        setCached(cacheKey, { metrics: metricsRes, timeOfDay: timeOfDayRes });
      })
      .catch(() => {
        if (!cancelled) {
          setMetrics({});
          setTimeOfDay({});
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(filters)]);

  const byHour = timeOfDay.byHour || [];
  const byDayOfWeek = timeOfDay.byDayOfWeek || [];

  return (
    <div className="p-4 mt-3">
      {filterBar}
      <Row>
        <KpiTile index={0} label={typography.TOTAL_USERS} value={metrics.totalUsersAllTime} loading={loading} colorClass="primary-gradient-bg" />
        <KpiTile index={1} label={typography.ENGAGED_USERS} value={metrics.engagedUsers} loading={loading} colorClass="bg-blue" />
        <KpiTile index={2} label={typography.IDLE_USERS} value={metrics.idleUsers} loading={loading} colorClass="bg-orange" />
        <KpiTile index={3} label={typography.SIGNUPS} value={metrics.signups} loading={loading} colorClass="bg-purple" />
        <KpiTile index={4} label={typography.DELETED_USERS} value={metrics.deletedUsers} loading={loading} colorClass="bg-orange" />
      </Row>

      <Row>
        <div className="col-xl-6 col-12">
          <motion.div initial="hidden" animate="visible" variants={panelVariants}>
            <Card className="mt-3">
              <CardBody>
                <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.TIME_OF_DAY}</h5>
                <ReactApexChart
                  type="bar"
                  height={260}
                  series={[{ name: "Activity", data: byHour.map((h) => h.count) }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 3, columnWidth: "60%" } },
                    colors: [GRAPH_COLORS.teal],
                    dataLabels: { enabled: false },
                    xaxis: {
                      categories: byHour.map((h) => `${h.hour}:00`),
                      labels: { style: { fontSize: "10px", fontFamily: "Poppins" }, rotate: -45 },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                    },
                    grid: { borderColor: GRAPH_COLORS.gridBorder },
                  }}
                />
              </CardBody>
            </Card>
          </motion.div>
        </div>
        <div className="col-xl-6 col-12">
          <motion.div initial="hidden" animate="visible" variants={panelVariants}>
            <Card className="mt-3">
              <CardBody>
                <h5 className="mb-3 font-family-lexend standard-typo-14-500-ld">{typography.DAY_OF_WEEK}</h5>
                <ReactApexChart
                  type="bar"
                  height={260}
                  series={[{ name: "Activity", data: byDayOfWeek.map((d) => d.count) }]}
                  options={{
                    chart: { toolbar: { show: false } },
                    plotOptions: { bar: { borderRadius: 4, columnWidth: "40%" } },
                    colors: [GRAPH_COLORS.teal],
                    dataLabels: { enabled: false },
                    xaxis: {
                      categories: byDayOfWeek.map((d) => DOW_LABELS[d.dayOfWeek] ?? d.dayOfWeek),
                      labels: { style: { fontSize: "12px", fontFamily: "Poppins" } },
                      axisBorder: { show: false },
                      axisTicks: { show: false },
                    },
                    grid: { borderColor: GRAPH_COLORS.gridBorder },
                  }}
                />
              </CardBody>
            </Card>
          </motion.div>
        </div>
      </Row>
    </div>
  );
}
